<template>
  <!-- prettier-ignore -->
  <div :class="classes">
    <label>
      <div class="input__title">{{ autoTitle }}</div>

      <div class="input-elements">
        <div
          v-for="(value, index) in values"
          class="input-element">
          <input
            :key="`code-${index}`"
            :ref="references[index]"
            v-bind="$attrs"
            :data-id="index"
            :pattern="numeric ? '[0-9]' : null"
            :required="isRequired"
            :type="numeric ? 'tel' : 'text'"
            :value="value"
            maxlength="1"
            placeholder="0"
            v-on="$listeners"
            @focus="$event.target.select()"
            @input="handleChange"
            @keyup.backspace.prevent="remove"
            @keyup.down.prevent
            @keyup.up.prevent
            @keyup.left.prevent="select(index - 1)"
            @keyup.right.prevent="select(index + 1)"
          />
        </div>
      </div>

      <div class="input__hint">{{ autoHint }}</div>
    </label>
    <slot />
  </div>
</template>

<script>
export default {
  name: 'OvpCodeInput',

  model: {
    prop: 'code',
    event: 'update'
  },
  inheritAttrs: false,

  props: {
    code: {
      type: [String, Number],
      default: null
    },

    hint: {
      type: String,
      default: ''
    },

    length: {
      type: Number,
      default: 4
    },

    numeric: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'light'
    },

    title: {
      type: String,
      default: ''
    },

    validate: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      focused: false,
      values: []
    }
  },

  mounted() {
    this.initializeValues()
  },

  watch: {
    code(code) {
      if (code === '' || code === null) {
        this.values = []
        this.initializeValues()
      }
    }
  },

  computed: {
    autoTitle() {
      return `${this.title} ${this.isRequired ? '*' : ''}`
    },

    autoHint() {
      const errors = Object.keys(this.validate?.$params ?? {}).filter(error => !this.validate[error])

      if (this.invalid && errors.length > 0) {
        return this.$t(`form.error.${errors[0]}`)
      } else {
        return this.hint
      }
    },

    classes() {
      return {
        'input': true,
        'input--invalid': this.invalid,
        [`input--${this.theme}`]: true
      }
    },

    invalid() {
      return this.validate?.$invalid && this.validate?.$error
    },

    isRequired() {
      return this.required || this.validate?.required !== undefined
    },

    references() {
      const references = []
      for (let i = 0; i < this.length; i++) {
        references.push(`input_${i}`)
      }
      return references
    }
  },

  methods: {
    emitUpdate(values) {
      this.values = values
      let code = values.join('')

      if (code === '') {
        code = null
      }

      this.$emit('update', code)
      if (code?.length >= this.length) {
        this.validate?.$touch()
        this.$emit('completed', code)
      }
    },

    initializeValues() {
      const code = this.code?.toString()
      if (code?.length) {
        for (let i = 0; i < this.length; i++) {
          this.values.push(code[i] || '')
        }
      } else {
        this.values = Array(this.length).fill('')
      }

      this.$nextTick(() => {
        this.$el.click()
        this.select(0)
      })
    },

    handleChange($event) {
      if (this.numeric) {
        $event.target.value = $event.target.value.replace(/[^\d]/gi, '')
      }
      if ($event.target.value === '' || (this.numeric && !$event.target.validity.valid)) {
        return
      }

      let nextIndex
      const index = parseInt($event.target.dataset.id)
      const value = $event.target.value
      const values = [...this.values]
      if (value.length <= 1) {
        nextIndex = index + 1
        values[index] = value
      } else {
        const split = value.split('')
        nextIndex = (nextIndex >= this.length ? this.length : value.length + index) - 1

        split.forEach((item, i) => {
          const cursor = index + i
          if (cursor < this.length) {
            values[cursor] = item
          }
        })
      }

      this.select(nextIndex)
      this.emitUpdate(values)
    },

    remove($event) {
      const index = parseInt($event.target.dataset.id)
      const nextIndex = this.values[index] ? index : this.references[index - 1] ? index - 1 : null
      if (this.references[nextIndex]) {
        const values = [...this.values]
        values[nextIndex] = ''

        this.select(nextIndex)
        this.emitUpdate(values)
      }
    },

    select(index) {
      const referenceIndex = this.references[index]
      if (this.$refs[referenceIndex]) {
        const element = this.$refs[referenceIndex][0]
        element.focus()
        element.select()

        this.validate?.$reset()
      }
    }
  }
}
</script>

<style name="mobile" scoped>
.input {
  position: relative;
  width: 100%;

  &__title {
    font-size: 15px;
    line-height: 16px;
    color: #999999;
    padding: 4px 10px;
    z-index: 1;
    cursor: text;
  }

  &-element {
    display: inline-block;
    margin: 0 5px 0 0;
    cursor: text;
    border-bottom: 1px solid var(--c-black);
    transition: border-bottom-color .2s ease-in-out;

    &:last-child {
      margin: 0;
    }
  }

  &-element input {
    width: 50px;
    height: 50px;
    font-size: 20px;
    text-align: center;
    color: var(--c-black);
    background: none;
    border: none;
    outline: none;
    border-bottom: 1px solid transparent;
    transition: border-bottom .2s ease-in-out;

    &::-ms-clear {
      display: none;
    }

    &:focus {
      border-bottom: 1px solid var(--c-black);
    }
  }

  &__hint {
    font-size: 15px;
    line-height: 20px;
    color: #999999;
    padding: 4px 10px;

    transition: color 0.2s ease-in-out;
  }

  &--invalid {
    .input-element, .input-element input:focus {
      border-bottom-color: var(--c-error);
    }

    .input__title,
    .input__hint {
      color: var(--c-error);
    }
  }

  &--dark {
    .input-element {
      background-color: var(--c-warm-gray);
    }
  }

  &--light {
    .input-element {
      background-color: var(--c-white);
    }
  }
}
</style>
